import { RecommendationInfoCardTooltip } from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/_table/fragment/RecommendationInfoCard';
import { DateTableOrderedCells, useDateTableColumns } from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/_table/fragment/DateTableColumns';
import { useMarketStateFilterInputTools } from '@app/site/secured/rateshopper/rates/provider/useMarketStateFilterInputTools';
import FormCheckbox from '@ui/checkbox/FormCheckbox';
import useToaster from '@app/component/overlays/toaster/ToasterHook';
import { useTranslation } from '@app/component/translation/Translation';
import { usePricingRights } from '@util/PricingRightsHook';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import CalendarDay from '@app/state/calendar/CalendarDay';
import { useSelectedCompanyHotel } from '@app/site/secured/rateshopper/rates/provider/useSelectedCompanyHotel';
import Utilities from '@util/Utilities';
import { Tooltip } from 'react-tooltip';
import { PlusCircleIcon, MinusCircleIcon } from '@heroicons/react/24/outline';
import { useMarketStateTools } from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/common/hook/useMarketStateTools';
import MarketStateSnapshotDateBasedTableRowFragment, {
	clearRecommendationDateDrag,
	findRecommendedPrice,
} from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/_table/fragment/MarketStateSnapshotDateBasedTableRowFragment';
import { useSaveCompanyHotelCalendarTableSettingsMutation } from '@app/model/company_hotel/service/CompanyHotelSettingsServiceHooks';
import { PICKUP_WINDOWS, PickupWindow } from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/_table/fragment/PickupCell';
import CheckboxMenu from '@ui/menu/CheckboxMenu';
import { findValidRecommendationStates } from '@app/site/secured/rateshopper/rates/dashboard/fragment/__market-state/_table/fragment/MarketStateSnapshotDateBasedTableRowFragment.utilities';

const DEFAULT_PICKUP_WINDOWS: PickupWindow[] = [7];
const MAX_PICKUP_WINDOWS = 2;

const OPEN_ALL_DETAILS_BATCH_SIZE = 4;
const widePriceColumnClass = (length: number) =>
	length > 12 ? 'w-52' : length > 8 ? 'w-40' : length > 5 ? 'w-32' : undefined;

export const MarketStateSnapshotDateBasedTable: React.FunctionComponent<{
	setSelectedCalendarDay: (calendarDay: CalendarDay) => any;
	selectedStatDates: string[];
	setSelectedStatDates: React.Dispatch<React.SetStateAction<string[]>>;
	bulkFailureReasons: ReadonlyMap<string, string>;
}> = ({ setSelectedCalendarDay, selectedStatDates, setSelectedStatDates, bulkFailureReasons }) => {
	const { companyHotel } = useSelectedCompanyHotel()!;
	const { filter } = useMarketStateFilterInputTools();
	const visibleCompetitors = companyHotel.visibleCompetitors(filter.competitorTagIds);
	const { canViewRecommendations, canChangePrices } = usePricingRights(companyHotel);
	const tools = useMarketStateTools();
	const columns = useDateTableColumns();
	const monthSnapshotStates = useMemo(() => tools.marketStateDailySnapshotStates.filter(({ calendarDay }) =>
		calendarDay.index.year === tools.calendarMonth.index.year
		&& calendarDay.index.month === tools.calendarMonth.index.month
	), [tools.marketStateDailySnapshotStates, tools.calendarMonth.index.year, tools.calendarMonth.index.month]);
	// Include collapsed rooms, but keep each hotel's width independent of other hotels.
	// Snapshot data fixes the widths before expansion; editor state cannot resize them.
	const priceColumnWidths = useMemo(() => {
		const lengths = new Map<number, number>();
		let recommendationLength = 0;
		for (const day of monthSnapshotStates) {
			for (const snapshot of day.marketStateDailySnapshot?.current.competitorSnapshots ?? []) {
				for (const room of snapshot.lowestItemPerHotelIdAndRoomIds) {
					const price = room.lowestItem?.item?.price;
					if (price) lengths.set(snapshot.competitorId,
						Math.max(lengths.get(snapshot.competitorId) ?? 0, Utilities.formatPrice(price).length));
				}
			}
			for (const recommendation of findValidRecommendationStates(day, companyHotel, canViewRecommendations)) {
				const price = findRecommendedPrice(day, recommendation, recommendation.roomMappingType);
				if (price !== undefined) recommendationLength = Math.max(recommendationLength,
					Utilities.formatAmount(price, companyHotel.settings.main.currency, 0).length);
			}
		}
		return {
			hotels: new Map([...lengths].map(([id, length]) => [id, widePriceColumnClass(length) ?? 'w-20'])),
			recommendation: widePriceColumnClass(recommendationLength) ?? 'w-24',
		};
	}, [monthSnapshotStates, companyHotel, canViewRecommendations]);

	const [detailsRequest, setDetailsRequest] = useState({ open: false, requestId: 0 });
	const [openAllDetailsCount, setOpenAllDetailsCount] = useState(0);
	const canSubmitPrices = canChangePrices && companyHotel.priceRecommendationSettings?.canSubmitPrices === true && companyHotel.pricingIntegrationActive === true;

	// The Pickup of the month: one query, the stored availability totals of the latest recommendation per date.
	const showPickup = canViewRecommendations;
	const paceByStatDate = columns.availability;

	// The Pickup Windows, 1 or 2, saved per hotel next to the table view.
	const savedWindows = companyHotel.settings.calendarTable.pickupWindows;
	const pickupWindows = useMemo<PickupWindow[]>(() => {
		const valid = (savedWindows ?? []).filter((window): window is PickupWindow => (PICKUP_WINDOWS as readonly number[]).includes(window));
		return valid.length > 0 ? valid.slice(0, MAX_PICKUP_WINDOWS).sort((a, b) => PICKUP_WINDOWS.indexOf(a) - PICKUP_WINDOWS.indexOf(b)) : DEFAULT_PICKUP_WINDOWS;
	}, [savedWindows]);
	const toaster = useToaster();
	const { t } = useTranslation();
	const savePickupWindows = useSaveCompanyHotelCalendarTableSettingsMutation({ onError: (error: string) => toaster.error('Pickup', t(error)) });
	const toggleWindow = useCallback((window: PickupWindow) => {
		if (savePickupWindows.isPending) return;
		const next = pickupWindows.includes(window)
			? pickupWindows.filter(value => value !== window)
			: [...pickupWindows, window].sort((a, b) => PICKUP_WINDOWS.indexOf(a) - PICKUP_WINDOWS.indexOf(b));
		if (next.length === 0 || next.length > MAX_PICKUP_WINDOWS) return;
		savePickupWindows.mutate({ companyHotelId: companyHotel.id, input: { view: companyHotel.settings.calendarTable.view, pickupWindows: next } });
	}, [companyHotel.id, companyHotel.settings.calendarTable.view, pickupWindows, savePickupWindows]);

	const selectableStatDates = useMemo(
		() => monthSnapshotStates
			.filter(row => canSubmitPrices && !row.calendarDay.isPast)
			.map(row => row.calendarDay.display),
		[canSubmitPrices, monthSnapshotStates]
	);
	const selectableStatDateSet = useMemo(() => new Set(selectableStatDates), [selectableStatDates]);
	const selectedStatDateSet = useMemo(() => new Set(selectedStatDates), [selectedStatDates]);
	const hasSelectableDates = selectableStatDates.length > 0;
	const allDatesSelected = hasSelectableDates && selectableStatDates.every(statDate => selectedStatDateSet.has(statDate));
	const toggleAllRecommendations = useCallback(() => {
		if (allDatesSelected) {
			setSelectedStatDates(statDates => statDates.filter(statDate => !selectableStatDateSet.has(statDate)));
			return;
		}

		setSelectedStatDates(statDates => Array.from(new Set([...statDates, ...selectableStatDates])));
	}, [
		allDatesSelected,
		selectableStatDates,
		selectableStatDateSet,
		setSelectedStatDates,
	]);
	const toggleRecommendationDate = useCallback(
		(statDate: string, selected: boolean) => {
			if (!selectableStatDateSet.has(statDate)) return;

			if (selected) {
				setSelectedStatDates(selectedDates => Array.from(new Set([...selectedDates, statDate])));
				return;
			}

			setSelectedStatDates(selectedDates => selectedDates.filter(selectedDate => selectedDate !== statDate));
		},
		[selectableStatDateSet, setSelectedStatDates]
	);
	useEffect(() => {
		window.addEventListener('mouseup', clearRecommendationDateDrag);
		return () => window.removeEventListener('mouseup', clearRecommendationDateDrag);
	}, []);
	useEffect(() => {
		if (!detailsRequest.open) {
			setOpenAllDetailsCount(0);
			return;
		}

		if (openAllDetailsCount >= monthSnapshotStates.length) return;

		const timeout = window.setTimeout(() => {
			setOpenAllDetailsCount(count =>
				Math.min(count + OPEN_ALL_DETAILS_BATCH_SIZE, monthSnapshotStates.length)
			);
		}, 16);

		return () => window.clearTimeout(timeout);
	}, [detailsRequest.open, openAllDetailsCount, monthSnapshotStates.length]);

	const toggleAllDetails = useCallback(() => {
		const open = !detailsRequest.open;
		setDetailsRequest(request => ({ open, requestId: request.requestId + 1 }));
		setOpenAllDetailsCount(open ? OPEN_ALL_DETAILS_BATCH_SIZE : 0);
	}, [detailsRequest.open]);

	const openAllDetails = detailsRequest.open;

	return (
		<>
		<RecommendationInfoCardTooltip />
		<table aria-label="Daily rates" className="rates-date-table w-full table-fixed divide-y divide-gray-300">
			<colgroup>
				<col className="w-8" /><col className="w-24" /><col className="w-8" /><col className="w-8" />
				<col className={priceColumnWidths.hotels.get(companyHotel.mainHotel.id) ?? 'w-20'} />
                <DateTableOrderedCells
                    recommendation={<col className={priceColumnWidths.recommendation} />}
                    pickup={<col className={pickupWindows.length > 1 ? 'w-24' : 'w-16'} />}
                    availability={<col className="w-20" />}
                    median={<col className="w-20" />}
                    competitors={visibleCompetitors.map(competitor =>
                        <col key={competitor.id} className={priceColumnWidths.hotels.get(competitor.id) ?? 'w-20'} />)}
                />
			</colgroup>
			<thead className="sticky-table-header">
				<tr>
					<th
						scope="col"
						className="px-2 py-3.5 text-left text-sm font-semibold text-gray-900"
					>
						{hasSelectableDates && (
							<FormCheckbox name="selectAllRecommendationDates" label="Select all dates" labelExtraClassName="sr-only"
								checked={allDatesSelected} onChange={toggleAllRecommendations} />
						)}
					</th>
					<th
						scope="col"
						className="px-2 py-3.5 text-left text-sm font-semibold text-gray-900"
					>
						Date
					</th>
					<th
						scope="col"
						className="px-2 py-3.5 text-left text-sm font-semibold text-gray-900"
					>
						<a
							className="hover:text-blue-500 cursor-pointer"
							data-tooltip-id="open-all-details"
							data-tooltip-content={openAllDetails ? 'Close all details' : 'Open all details'}
							onClick={toggleAllDetails}
						>
							{openAllDetails ? (
								<MinusCircleIcon className="w-4 h-4" />
							) : (
								<PlusCircleIcon className="w-4 h-4" />
							)}
						</a>
						<Tooltip id="open-all-details" />
					</th>
					<th
						scope="col"
						className="px-2 py-3.5 text-left text-sm font-semibold text-gray-900"
					></th>
					<th scope="col" className="px-2 py-3.5 text-left text-xs font-semibold text-gray-900">
						<span
							className="block truncate cursor-pointer"
							data-tooltip-id="main_hotel_name"
							data-tooltip-content={companyHotel.mainHotel.competitors[0].hotel.name}
							data-tooltip-place="right"
						>
							{Utilities.trim(companyHotel.mainHotel.competitors[0].hotel.name, 22)}
						</span>
						<Tooltip id="main_hotel_name" />
					</th>
                    <DateTableOrderedCells
                        recommendation={<th scope="col" className="whitespace-nowrap px-2 py-3.5 text-left text-xs font-semibold text-gray-900">Rec Price</th>}
                        pickup={<th scope="col" className="whitespace-nowrap px-2 py-3.5 text-center text-xs font-semibold text-gray-900">
                            <span className="relative inline-block"><CheckboxMenu label={<>Pickup<br />{pickupWindows.map(window => window === 0 ? 'last chg' : `${window}d`).join(' · ')}</>}
                                options={PICKUP_WINDOWS.map(window => ({ value: window, label: window === 0 ? 'Last change' : `${window} days` }))}
                                selected={pickupWindows} onToggle={toggleWindow} max={MAX_PICKUP_WINDOWS} min={1}
                                hint={`Pick up to ${MAX_PICKUP_WINDOWS} windows`} title="Pick the Pickup windows" /></span>
                        </th>}
                        availability={<th scope="col" className="whitespace-nowrap px-2 py-3.5 text-center text-xs font-semibold text-gray-900">Availability</th>}
                        median={<th scope="col" className="px-2 py-3.5 text-left text-sm font-semibold text-gray-900">{companyHotel.comparisonLabel()}</th>}
                        competitors={visibleCompetitors.map(competitor =>
                            <th scope="col" key={competitor.id} className="px-2 py-3.5 text-left text-xs font-semibold text-gray-900">
                                <span className="block truncate cursor-pointer"
                                    data-tooltip-id={`competitor_${competitor.id}_hotel_name`}
                                    data-tooltip-content={competitor.name} data-tooltip-place="left">
                                    {Utilities.trim(competitor.name, 22)}
                                </span>
                                <Tooltip id={`competitor_${competitor.id}_hotel_name`} />
                            </th>)}
                    />
				</tr>
			</thead>
			<tbody className="divide-y divide-gray-200 bg-white">
				{monthSnapshotStates.map(
					(dailySnapshotState, index) => {
						const statDate = dailySnapshotState.calendarDay.display;

						return (
							<MarketStateSnapshotDateBasedTableRowFragment
								key={dailySnapshotState.calendarDay.display}
								dailySnapshotState={dailySnapshotState}
								onSelectedCalendarDay={setSelectedCalendarDay}
								detailsRequestId={detailsRequest.requestId}
								enforceOpenDetails={openAllDetails && index < openAllDetailsCount}
								dateSelectable={selectableStatDateSet.has(statDate)}
								dateSelected={selectedStatDateSet.has(statDate)}
								bulkFailureReason={bulkFailureReasons.get(statDate)}
								onRecommendationSelectedChange={toggleRecommendationDate}
								pace={paceByStatDate.get(statDate)}
								pickupWindows={pickupWindows}
								showPickup={showPickup}
								canSubmitPrices={canSubmitPrices}
							/>
						);
					}
				)}
			</tbody>
			{canViewRecommendations && (
				<tfoot>
					<tr>
						<td
							colSpan={5 + columns.visible.length}
							className="px-2 py-2 text-xs italic text-gray-500"
						>
							* The percentage refers to the cheapest available room that has a recommendation.
							Expand the date to see each room.
						</td>
					</tr>
				</tfoot>
			)}
		</table>
		</>
	);
};
